import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./apiClient";
import type {
  TemplateFormSettings,
  TemplateFormTypes,
  UpdateFormQuestion,
  UpdateTemplateFormSettingsPayload,
} from "./templateFormSettings.types";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { prefetch } from "./prefetch";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/admin/form`;

const templateFormsSettingKeys = {
  byId: (id: string) => [id] as const,
  templateFormTypes: () => ["templateFormTypes"] as const,
};

/** Get template form settings by specific id */
const templateFormSettingsByIdQueryOptions = (id: string) => {
  return queryOptions({
    queryKey: templateFormsSettingKeys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}/settings`) as Promise<TemplateFormSettings>,
    throwOnError: true,
  });
};
export const useGetTemplateFormSettingsById = (id: string) => useQuery(templateFormSettingsByIdQueryOptions(id));
export const prefetchTemplateFormSettingsById = (id: string) => prefetch(templateFormSettingsByIdQueryOptions(id));
/** Update template form settings */
export const useUpdateTemplateFormSettings = () => {
  const queryClient = useQueryClient();

  const updateFormSettings = ({ id, payload }: { id: string; payload: UpdateTemplateFormSettingsPayload }) =>
    api.post(`${BASE_ENDPOINT}/${id}/settings`, payload);

  return useMutation({
    mutationFn: updateFormSettings,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Update template form question */
export const useUpdateTemplateFormQuestion = () => {
  const queryClient = useQueryClient();

  const updateFormQuestion = ({
    id,
    questionId,
    payload,
  }: {
    id: string;
    questionId: string;
    payload: UpdateFormQuestion[];
  }) =>
    api.patch(`${BASE_ENDPOINT}/${id}/settings/question/${questionId}`, payload, {
      headers: { "Content-Type": "application/json-patch+json" },
    }) as Promise<TemplateFormSettings[]>;

  return useMutation({
    mutationFn: updateFormQuestion,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Get template form types */
const templateFormTypesQueryOptions = () => {
  return queryOptions({
    queryKey: templateFormsSettingKeys.templateFormTypes(),
    queryFn: () => api.get(BASE_ENDPOINT) as Promise<TemplateFormTypes>,
    throwOnError: true,
  });
};
export const useGetTemplateFormTypes = () => useQuery(templateFormTypesQueryOptions());
export const prefetchTemplateFormTypes = () => prefetch(templateFormTypesQueryOptions());
